import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, CheckCheck, ArrowRight } from 'lucide-react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';
import { useAuth } from '@/contexts/AuthContext';
import {
  fetchNotifications,
  markNotificationRead,
  markAllNotificationsRead,
  type AppNotification,
} from '@/lib/api/notifications';
import { formatDistanceToNow } from 'date-fns';

const NOTIFICATION_TYPE_ICON: Record<string, string> = {
  pm_auto_created: '🔧',
  fix_validation_failed: '⚠️',
  blocked_escalation: '🚫',
  line_down: '🚨',
  advisory_created: '📋',
};

export function NotificationInbox() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const qc = useQueryClient();

  const { data: notifications = [] } = useQuery<AppNotification[]>({
    queryKey: ['notifications', user?.id],
    queryFn: fetchNotifications,
    enabled: !!user,
    staleTime: 30_000,
    refetchInterval: 30_000, // fallback poll — realtime handles instant delivery
  });

  const unreadCount = notifications.filter(n => !n.read).length;

  const { mutate: markRead } = useMutation({
    mutationFn: markNotificationRead,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['notifications', user?.id] }),
  });

  const { mutate: markAll } = useMutation({
    mutationFn: markAllNotificationsRead,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['notifications', user?.id] }),
  });

  const handleClick = useCallback((n: AppNotification) => {
    if (!n.read) markRead(n.id);
    if (n.link) navigate(n.link);
  }, [markRead, navigate]);

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="relative h-8 w-8"
          aria-label={`Notifications${unreadCount > 0 ? ` (${unreadCount} unread)` : ''}`}
        >
          <Bell className="h-4 w-4" />
          {unreadCount > 0 && (
            <span className="absolute top-0.5 right-0.5 h-4 w-4 rounded-full bg-destructive flex items-center justify-center">
              <span className="text-[9px] font-bold text-white leading-none">
                {unreadCount > 9 ? '9+' : unreadCount}
              </span>
            </span>
          )}
        </Button>
      </DropdownMenuTrigger>

      <DropdownMenuContent align="end" className="w-80 p-0 max-h-[480px] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-3 py-2.5 border-b">
          <span className="text-sm font-semibold">
            Notifications
            {unreadCount > 0 && (
              <span className="ml-2 text-xs font-medium text-molten-deep">{unreadCount} new</span>
            )}
          </span>
          {unreadCount > 0 && (
            <button
              onClick={() => markAll()}
              className="flex items-center gap-1 text-[11px] text-muted-foreground hover:text-foreground transition-colors"
            >
              <CheckCheck className="h-3.5 w-3.5" />
              Mark all read
            </button>
          )}
        </div>

        {/* Notification list */}
        <div className="overflow-y-auto flex-1">
          {notifications.length === 0 ? (
            <div className="px-4 py-8 text-center">
              <Bell className="h-6 w-6 mx-auto mb-2 text-muted-foreground/30" />
              <p className="text-sm text-muted-foreground">No notifications yet</p>
            </div>
          ) : (
            notifications.map((n, i) => (
              <div key={n.id}>
                {i > 0 && <DropdownMenuSeparator className="my-0" />}
                <button
                  onClick={() => handleClick(n)}
                  className={cn(
                    'w-full text-left flex items-start gap-2.5 px-3 py-2.5 hover:bg-muted/50 transition-colors',
                    !n.read && 'bg-molten/5',
                  )}
                >
                  <span className="text-base leading-none mt-0.5 shrink-0">
                    {NOTIFICATION_TYPE_ICON[n.type] ?? '🔔'}
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5">
                      <p className={cn('text-xs leading-snug', !n.read ? 'font-semibold' : 'font-medium text-muted-foreground')}>
                        {n.title}
                      </p>
                      {!n.read && (
                        <span className="h-1.5 w-1.5 rounded-full bg-molten shrink-0" />
                      )}
                    </div>
                    <p className="text-[11px] text-muted-foreground/80 mt-0.5 line-clamp-2">{n.message}</p>
                    <p className="text-[10px] text-muted-foreground/50 mt-1">
                      {formatDistanceToNow(new Date(n.created_at), { addSuffix: true })}
                    </p>
                  </div>
                  {n.link && (
                    <ArrowRight className="h-3.5 w-3.5 text-muted-foreground/40 shrink-0 mt-1" />
                  )}
                </button>
              </div>
            ))
          )}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
